import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";

export default function CoachChangePassword() {
  const { user, changePassword, logout } = useAuth();
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const forced = !!user?.must_change_password;

  const submit = async (e) => {
    e.preventDefault();
    if (newPw.length < 6) return toast.error("New password must be at least 6 characters");
    if (newPw !== confirmPw) return toast.error("Passwords do not match");
    setLoading(true);
    try {
      await changePassword(currentPw, newPw);
      toast.success("Password updated");
      nav("/coach");
    } catch (err) { toast.error(apiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-xl" data-testid="coach-change-password-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 border border-brand-blue/50 flex items-center justify-center brand-blue"><KeyRound size={18} /></div>
        <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange">// {forced ? "First-time login" : "Rotate password"}</div>
      </div>
      <h1 className="font-display text-4xl md:text-5xl font-black uppercase leading-none tracking-tighter">
        {forced ? <>Set a new<br /><span className="brand-blue">private password</span>.</> : <>Rotate your<br /><span className="brand-blue">password</span>.</>}
      </h1>
      <p className="mt-4 text-sm text-white/60">Signed in as <span className="brand-blue">{user?.email}</span></p>

      <form onSubmit={submit} className="mt-8 space-y-4">
        <input type="password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} required data-testid="coach-cp-current"
          placeholder={forced ? "Temporary password issued by admin" : "Current password"}
          className="w-full bg-transparent border border-white/20 py-3 px-4 text-white placeholder-white/40 focus:outline-none focus:border-brand-blue" />
        <input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} required minLength={6} data-testid="coach-cp-new"
          placeholder="New password (min 6 chars)"
          className="w-full bg-transparent border border-white/20 py-3 px-4 text-white placeholder-white/40 focus:outline-none focus:border-brand-blue" />
        <input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} required minLength={6} data-testid="coach-cp-confirm"
          placeholder="Confirm new password"
          className="w-full bg-transparent border border-white/20 py-3 px-4 text-white placeholder-white/40 focus:outline-none focus:border-brand-blue" />
        <button type="submit" disabled={loading} data-testid="coach-cp-submit"
          className="w-full bg-brand-blue text-white py-4 font-bold text-sm uppercase tracking-widest hover:glow-blue disabled:opacity-50">
          {loading ? "…" : "Update Password"}
        </button>
        {forced && (
          <button type="button" onClick={async () => { await logout(); nav("/coach/login"); }}
            className="w-full text-xs font-mono uppercase tracking-widest text-white/50 hover:text-white pt-2">
            Cancel & Logout
          </button>
        )}
      </form>
    </div>
  );
}
